import { useMemo, useState } from "react";
import {
  Button,
  Form,
  Image,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Upload,
  message,
} from "antd";
import { PlusOutlined, UploadOutlined } from "@ant-design/icons";
import {
  useAdminDeleteLobbyBannerMutation,
  useAdminGetLobbyBannersQuery,
  useAdminSaveLobbyBannerMutation,
} from "../../../store/service/lobbyBannerService";

const SECTIONS = [
  { key: "banner", label: "Main Banners" },
  { key: "trending", label: "Now Trending" },
  { key: "casino", label: "Our Casino" },
  { key: "new_games", label: "New Games" },
];

const emptyForm = {
  section: "banner",
  alt_text: "",
  link_url: "",
  sort_order: 0,
  is_active: true,
};

const LobbyBannerAdmin = () => {
  const [section, setSection] = useState("banner");
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [fileList, setFileList] = useState([]);
  const [form] = Form.useForm();

  const { data, isLoading } = useAdminGetLobbyBannersQuery(section);
  const [saveBanner, { isLoading: saving }] = useAdminSaveLobbyBannerMutation();
  const [deleteBanner] = useAdminDeleteLobbyBannerMutation();

  const rows = useMemo(
    () => (data?.data || []).filter((row) => row.section === section),
    [data, section],
  );

  const openAdd = () => {
    setEditing(null);
    setFileList([]);
    form.setFieldsValue({ ...emptyForm, section });
    setModalOpen(true);
  };

  const openEdit = (row) => {
    setEditing(row);
    setFileList([]);
    form.setFieldsValue({
      section: row.section,
      alt_text: row.altText,
      link_url: row.linkUrl || "",
      sort_order: row.sortOrder,
      is_active: row.isActive,
    });
    setModalOpen(true);
  };

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      const formData = new FormData();
      if (editing?.id) formData.append("id", String(editing.id));
      formData.append("section", values.section);
      formData.append("alt_text", values.alt_text || "");
      formData.append("link_url", values.link_url || "");
      formData.append("sort_order", String(values.sort_order ?? 0));
      formData.append("is_active", values.is_active ? "1" : "0");

      if (fileList[0]?.originFileObj) {
        formData.append("image", fileList[0].originFileObj);
      } else if (!editing) {
        message.error("Please upload an image");
        return;
      }

      const res = await saveBanner(formData).unwrap();
      message.success(res.message || "Saved");
      setModalOpen(false);
      setFileList([]);
    } catch (err) {
      message.error(err?.data?.message || "Save failed");
    }
  };

  const onDelete = async (id) => {
    try {
      await deleteBanner({ id }).unwrap();
      message.success("Deleted");
    } catch (err) {
      message.error(err?.data?.message || "Delete failed");
    }
  };

  const columns = [
    {
      title: "Preview",
      dataIndex: "imageUrl",
      width: 120,
      render: (url, row) => (
        <Image
          src={url}
          alt={row.altText}
          width={96}
          height={48}
          style={{ objectFit: "cover", borderRadius: 4 }}
        />
      ),
    },
    { title: "Title", dataIndex: "altText", key: "altText" },
    { title: "Link", dataIndex: "linkUrl", key: "linkUrl", ellipsis: true },
    { title: "Order", dataIndex: "sortOrder", width: 70 },
    {
      title: "Active",
      dataIndex: "isActive",
      width: 80,
      render: (v) => (v ? <Tag color="green">Yes</Tag> : <Tag>No</Tag>),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, row) => (
        <Space>
          <Button size="small" onClick={() => openEdit(row)}>
            Edit
          </Button>
          <Button size="small" danger onClick={() => onDelete(row.id)}>
            Delete
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 16 }}>
      <h2 style={{ marginBottom: 12 }}>Lobby Banners</h2>
      <p style={{ marginBottom: 16, color: "#666" }}>
        Manage pre-login home banners, trending cards, casino tiles and new games from here.
      </p>

      <Tabs
        activeKey={section}
        onChange={setSection}
        items={SECTIONS.map((s) => ({
          key: s.key,
          label: s.label,
          children: (
            <>
              <Button type="primary" icon={<PlusOutlined />} onClick={openAdd} style={{ marginBottom: 12 }}>
                Add {s.label}
              </Button>
              <Table
                rowKey="id"
                loading={isLoading}
                columns={columns}
                dataSource={rows}
                pagination={false}
                scroll={{ x: 800 }}
              />
            </>
          ),
        }))}
      />

      <Modal
        title={editing ? "Edit Banner" : "Add Banner"}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        onOk={onSave}
        confirmLoading={saving}
        width={560}>
        <Form form={form} layout="vertical">
          <Form.Item name="section" label="Section" rules={[{ required: true }]}>
            <Select
              options={SECTIONS.map((s) => ({ value: s.key, label: s.label }))}
              disabled={!!editing}
            />
          </Form.Item>
          <Form.Item name="alt_text" label="Title / Alt text">
            <Input placeholder="Super Over 2" />
          </Form.Item>
          <Form.Item name="link_url" label="Link URL (optional)">
            <Input placeholder="/play/casino/3030" />
          </Form.Item>
          <Form.Item name="sort_order" label="Sort order">
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item label={editing ? "Replace image (optional)" : "Image"} required={!editing}>
            <Upload
              accept="image/*"
              maxCount={1}
              beforeUpload={() => false}
              fileList={fileList}
              onChange={({ fileList: next }) => setFileList(next)}
              listType="picture-card">
              {fileList.length ? null : (
                <div>
                  <UploadOutlined />
                  <div style={{ marginTop: 8 }}>Upload</div>
                </div>
              )}
            </Upload>
            {editing?.imageUrl ? (
              <Image
                src={editing.imageUrl}
                alt={editing.altText}
                width={180}
                style={{ marginTop: 8, borderRadius: 6 }}
              />
            ) : null}
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LobbyBannerAdmin;
